if (!customElements.get("product-form")) {
  const directCartProductForms = new Set();
  let directCartSyncPromise = null;
  let directCartSyncFrame = null;
  let directCartSyncInitialized = false;

  const getVariantCartQuantity = (cart, variantId) => {
    if (!Array.isArray(cart?.items)) return 0;

    const normalizedVariantId = String(variantId);

    return cart.items.reduce((quantity, item) => {
      return String(item.variant_id || item.id) === normalizedVariantId
        ? quantity + item.quantity
        : quantity;
    }, 0);
  };

  const getProductCartQuantity = (cart, productId) => {
    if (!Array.isArray(cart?.items)) return 0;

    return cart.items.reduce((quantity, item) => {
      return String(item.product_id) === String(productId)
        ? quantity + item.quantity
        : quantity;
    }, 0);
  };

  const updateDirectCartProductForms = (cart) => {
    directCartProductForms.forEach((productForm) => {
      if (!productForm.isConnected) {
        directCartProductForms.delete(productForm);
        return;
      }

      if (!productForm.formInput) return;
      if (productForm.directCartUpdate && !productForm.quantityInput) return;
      if (
        typeof productForm.hasPendingDirectCartUpdate === "function" &&
        productForm.hasPendingDirectCartUpdate()
      ) {
        return;
      }

      const updatedQuantity = getVariantCartQuantity(
        cart,
        productForm.formInput.value,
      );
      const productQuantity = getProductCartQuantity(
        cart,
        productForm.productId,
      );

      productForm.currentDirectCartQuantity = updatedQuantity;
      productForm.syncCartLimitation(productQuantity, updatedQuantity);
      if (productForm.directCartUpdate) {
        productForm.quantityInput.value = updatedQuantity;
        productForm.updateQuantityCounterState();
      }
    });
  };

  const fetchAndSyncDirectCartProductForms = () => {
    if (!directCartProductForms.size || !routes.cart_url) return;
    if (directCartSyncPromise) return directCartSyncPromise;

    directCartSyncPromise = fetch(`${routes.cart_url}.js`, {
      headers: { Accept: "application/json" },
      cache: "no-store",
    })
      .then((response) => {
        if (!response.ok) throw new Error("Unable to fetch cart");
        return response.json();
      })
      .then((cart) => updateDirectCartProductForms(cart))
      .catch((error) => console.error(error))
      .finally(() => {
        directCartSyncPromise = null;
      });

    return directCartSyncPromise;
  };

  const updateProductFormSync = () => {
    if (directCartSyncFrame) return;

    directCartSyncFrame = requestAnimationFrame(() => {
      directCartSyncFrame = null;
      fetchAndSyncDirectCartProductForms();
    });
  };

  const initializeDirectCartSync = () => {
    if (directCartSyncInitialized) return;
    directCartSyncInitialized = true;

    window.addEventListener("pageshow", updateProductFormSync);

    if (typeof subscribe === "function") {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (
          event.cartData &&
          Array.isArray(event.cartData.items) &&
          typeof event.cartData.item_count === "number"
        ) {
          updateDirectCartProductForms(event.cartData);
          return;
        }

        updateProductFormSync();
      });
    }
  };

  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.formInput = this.form?.querySelector("[name=id]");
        if (this.formInput) this.formInput.disabled = false;
        this.form?.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart = document.querySelector("cart-drawer");
        this.cartType = this.cart?.dataset.cartType || "page";

        this.submitButton = this.querySelector('[type="submit"]');
        this.dynamicCheckoutButton = this.querySelector(
          ".shopify-payment-button",
        );
        this.body = document.querySelector("body");

        if (document.querySelector("cart-drawer"))
          this.submitButton?.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";
        this.directCartUpdate = this.dataset.directCartUpdate === "true";
        this.productId = this.dataset.productId;
        this.hasCartLimitation = Object.prototype.hasOwnProperty.call(
          this.dataset,
          "cartLimitation",
        );
        this.cartLimitation = this.hasCartLimitation
          ? Number(this.dataset.cartLimitation)
          : null;
        const initialCartLimitationRemaining = Number(
          this.dataset.cartLimitationRemaining,
        );
        this.cartLimitationRemaining = Number.isFinite(
          initialCartLimitationRemaining,
        )
          ? initialCartLimitationRemaining
          : null;
        this.quantityInput = this.querySelector(".js-counter-quantity");
        this.currentDirectCartQuantity = this.quantityInput
          ? parseInt(this.quantityInput.value, 10) || 0
          : 0;
        this.pendingDirectCartQuantity = null;
        this.directCartUpdateInProgress = false;

        this.handleErrorMessage = this.handleErrorMessage.bind(this);
        this.redirectAfterSubmit = this.redirectAfterSubmit.bind(this);
        this.disableLoadingInButton = this.disableLoadingInButton.bind(this);
        this.onDirectCartQuantityChange =
          this.onDirectCartQuantityChange.bind(this);
        this.onDirectCartQuantityKeydown =
          this.onDirectCartQuantityKeydown.bind(this);
        this.queueDirectCartQuantityChange =
          this.queueDirectCartQuantityChange.bind(this);
        this.flushDirectCartQuantityChange =
          this.flushDirectCartQuantityChange.bind(this);

        if (
          (this.directCartUpdate || this.hasCartLimitation) &&
          this.form &&
          (!this.directCartUpdate || this.quantityInput)
        ) {
          directCartProductForms.add(this);
          initializeDirectCartSync();
          updateProductFormSync();

          if (this.directCartUpdate) {
            const debouncedQuantityChange = debounce(
              this.flushDirectCartQuantityChange,
              ON_CHANGE_DEBOUNCE_TIMER,
            );

            this.form.addEventListener("change", (event) => {
              if (event.target !== this.quantityInput) return;
              this.queueDirectCartQuantityChange();
              debouncedQuantityChange();
            });
            this.quantityInput.addEventListener(
              "keydown",
              this.onDirectCartQuantityKeydown,
            );
          }
        }
      }

      disconnectedCallback() {
        if (this.directCartUpdate || this.hasCartLimitation) {
          directCartProductForms.delete(this);
        }
      }

      closeComplementaryProduct() {
        const complementaryOverlayPage = document.querySelector(
          ".wt__quick-buy--page-overlay",
        );
        if (this.body.classList.contains("quick-buy-page-overlay"))
          this.body.classList.remove("quick-buy-page-overlay");
        if (
          complementaryOverlayPage.classList.contains(
            "wt__quick-buy--page-overlay--open",
          )
        )
          complementaryOverlayPage.classList.remove(
            "wt__quick-buy--page-overlay--open",
          );
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.directCartUpdate) return;
        if (!this.submitButton) return;
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        if (this.hasCartLimitation) {
          if (this.cartLimitValidationInProgress) return;
          this.cartLimitValidationInProgress = true;
          await fetchAndSyncDirectCartProductForms();
          this.cartLimitValidationInProgress = false;

          const requestedQuantity = this.quantityInput
            ? parseInt(this.quantityInput.value, 10) || 0
            : 1;
          if (
            this.cartLimitationRemaining === 0 ||
            (this.cartLimitationRemaining !== null &&
              requestedQuantity > this.cartLimitationRemaining)
          ) {
            this.handleErrorMessage(
              window.cartStrings.quantityError.replace(
                "[quantity]",
                Math.max(0, this.cartLimitationRemaining),
              ),
            );
            return;
          }
        }

        this.handleErrorMessage();
        this.cart?.setActiveElement(document.activeElement);

        const loader = this.querySelector(".loading-overlay__spinner");

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        if (loader) loader.classList.remove("hidden");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        if (this.cart && typeof this.cart.getSectionsToRender === "function") {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((section) => section.id),
          );
        } else {
          formData.append("sections", []);
        }

        formData.append("sections_url", window.location.pathname);
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.error = true;

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");

              this.dispatchEvent(
                new CustomEvent("cart-drawer:refresh", {
                  detail: { response: response },
                  bubbles: true,
                  composed: true,
                }),
              );

              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButton.querySelector("span").classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest("quick-add-modal");
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    this.redirectAfterSubmit(response);
                  });
                },
                { once: true },
              );
              if (this.cartType === "drawer") quickAddModal.hide(true);
            } else {
              const isClosedCart = !document.body.classList.contains(
                "page-overlay-cart-on",
              );
              this.redirectAfterSubmit(response, isClosedCart);
            }
          })
          .catch((e) => {
            console.error(e);
            if (e instanceof TypeError && e.message.includes("'cart-drawer'")) {
              location.reload();
            }
          })
          .finally(() => {
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (
              !this.hasCartLimitation ||
              this.cartLimitationRemaining === null ||
              this.cartLimitationRemaining > 0
            ) {
              this.submitButton.removeAttribute("aria-disabled");
            }
            if (this.error) {
              this.disableLoadingInButton();
              return;
            }
            this.quick_add_container = document.querySelector(
              ".wt__quick-buy__container",
            );
            this.quick_add_overlay = document.querySelector(
              ".wt__quick-buy--page-overlay",
            );
            this.quick_add_product =
              this.quick_add_container.querySelector(".wt-product");
            this.loader = this.quick_add_container.querySelector(
              ".wt__quick-buy-loader",
            );

            if (!this.quick_add_container.classList.contains("hidden")) {
              // use handle and quickbuy closeCart()

              this.quick_add_product?.remove();
              this.quick_add_container.classList.remove(
                "wt__quick-buy__container--open",
              );
              this.quick_add_overlay.classList.remove(
                "wt__quick-buy--page-overlay--open",
              );
              document.body.classList.remove("quick-buy-page-overlay");
              this.loader.classList.remove("hidden");
            }

            // delay button loading
            setTimeout(() => {
              this.disableLoadingInButton();
            }, 200);
          });
      }

      disableLoadingInButton() {
        if (!this.submitButton) return;
        this.submitButton.classList.remove("loading");
        this.querySelector(".loading-overlay__spinner")?.classList.add("hidden");
      }

      onDirectCartQuantityKeydown(event) {
        if (event.key !== "Enter") return;

        event.preventDefault();
        this.queueDirectCartQuantityChange();
        this.onDirectCartQuantityChange();
      }

      onDirectCartQuantityChange() {
        this.flushDirectCartQuantityChange();
      }

      queueDirectCartQuantityChange() {
        if (!this.quantityInput) return null;

        const quantity = this.getValidatedQuantity();
        this.quantityInput.value = quantity;
        this.pendingDirectCartQuantity = quantity;

        return quantity;
      }

      flushDirectCartQuantityChange() {
        if (this.directCartUpdateInProgress) return;
        if (this.pendingDirectCartQuantity === null) return;
        if (!this.formInput || !this.quantityInput) return;

        const quantity = this.pendingDirectCartQuantity;
        this.pendingDirectCartQuantity = null;

        const previousQuantity = this.currentDirectCartQuantity;
        this.quantityInput.value = quantity;

        if (quantity === previousQuantity) return;

        this.directCartUpdateInProgress = true;
        this.handleErrorMessage();

        const variantId = this.formInput.value;
        const body = JSON.stringify({
          updates: {
            [variantId]: quantity,
          },
          sections: this.getDirectCartSectionsToRender(),
          sections_url: window.location.pathname,
        });

        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
          .then((response) => response.json())
          .then((response) => {
            const hasPendingQuantity = this.pendingDirectCartQuantity !== null;

            if (response.status || response.errors) {
              if (!hasPendingQuantity) {
                this.quantityInput.value = previousQuantity;
                this.handleErrorMessage(
                  response.description ||
                    response.errors ||
                    window.cartStrings.error,
                );
              }
              return;
            }

            const updatedQuantity = this.getVariantCartQuantity(
              response,
              variantId,
            );

            this.currentDirectCartQuantity = updatedQuantity;
            if (!hasPendingQuantity) this.quantityInput.value = updatedQuantity;

            if (!hasPendingQuantity && updatedQuantity !== quantity) {
              this.handleErrorMessage(
                window.cartStrings.quantityError.replace(
                  "[quantity]",
                  updatedQuantity,
                ),
              );
            }

            this.renderDirectCartUpdate(response);
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: "product-form",
              productVariantId: variantId,
              cartData: response,
            });
          })
          .catch((error) => {
            console.error(error);
            if (this.pendingDirectCartQuantity === null) {
              this.quantityInput.value = previousQuantity;
              this.handleErrorMessage(window.cartStrings.error);
            }
          })
          .finally(() => {
            this.directCartUpdateInProgress = false;
            this.flushDirectCartQuantityChange();
          });
      }

      getValidatedQuantity() {
        const parsedMax = parseInt(this.quantityInput.max, 10);
        const max = Number.isNaN(parsedMax) ? 999 : parsedMax;
        let quantity = parseInt(this.quantityInput.value, 10);

        if (Number.isNaN(quantity)) quantity = 0;

        return Math.max(0, Math.min(quantity, max));
      }

      hasPendingDirectCartUpdate() {
        return (
          this.directCartUpdateInProgress ||
          this.pendingDirectCartQuantity !== null
        );
      }

      getDirectCartSectionsToRender() {
        if (this.cart && typeof this.cart.getSectionsToRender === "function") {
          return this.cart.getSectionsToRender().map((section) => section.id);
        }

        return ["cart-icon-bubble"];
      }

      getVariantCartQuantity(cart, variantId) {
        if (!cart || !Array.isArray(cart.items)) return 0;

        return cart.items.reduce((quantity, item) => {
          const itemVariantId = String(item.variant_id || item.id);
          if (itemVariantId !== String(variantId)) return quantity;
          return quantity + item.quantity;
        }, 0);
      }

      syncCartLimitation(productQuantity, variantQuantity) {
        if (!this.hasCartLimitation || !Number.isFinite(this.cartLimitation)) {
          return;
        }

        const quantityOutsideCounter = this.directCartUpdate
          ? Math.max(0, productQuantity - variantQuantity)
          : productQuantity;
        const remaining = Math.max(
          0,
          this.cartLimitation - quantityOutsideCounter,
        );

        this.cartLimitationRemaining = remaining;

        if (this.quantityInput) {
          this.quantityInput.max = remaining;

          if (!this.directCartUpdate) {
            this.quantityInput.min = remaining > 0 ? 1 : 0;
            const currentQuantity = parseInt(this.quantityInput.value, 10) || 0;
            this.quantityInput.value =
              remaining > 0 ? Math.max(1, Math.min(currentQuantity, remaining)) : 0;
          }

          this.updateQuantityCounterState();
        }

        if (!this.directCartUpdate && this.submitButton) {
          if (remaining <= 0) {
            this.submitButton.disabled = true;
            this.submitButton.setAttribute("aria-disabled", "true");
            this.submitButton.dataset.cartLimitDisabled = "true";
          } else if (this.submitButton.dataset.cartLimitDisabled === "true") {
            this.submitButton.disabled = false;
            this.submitButton.removeAttribute("aria-disabled");
            delete this.submitButton.dataset.cartLimitDisabled;
          }
        }

        if (!this.directCartUpdate && this.dynamicCheckoutButton) {
          this.dynamicCheckoutButton.hidden = remaining <= 0;
        }

        if (!this.directCartUpdate && this.form?.id) {
          document
            .querySelectorAll("[data-cart-limit-form]")
            .forEach((externalSubmitButton) => {
              if (externalSubmitButton.dataset.cartLimitForm !== this.form.id) {
                return;
              }

              if (remaining <= 0) {
                externalSubmitButton.disabled = true;
                externalSubmitButton.dataset.cartLimitDisabled = "true";
              } else if (
                externalSubmitButton.dataset.cartLimitDisabled === "true"
              ) {
                externalSubmitButton.disabled = false;
                delete externalSubmitButton.dataset.cartLimitDisabled;
              }
            });
        }
      }

      updateQuantityCounterState() {
        this.quantityInput
          ?.closest("quantity-counter")
          ?.updateButtonStates?.();
      }

      renderDirectCartUpdate(response) {
        if (this.cart) {
          this.cart.classList.toggle("is-empty", response.item_count === 0);
        }

        if (this.cart && typeof this.cart.renderContents === "function" && response.sections) {
          this.cart.renderContents(response, false);
          return;
        }

        this.dispatchEvent(
          new CustomEvent("cart-drawer:refresh", {
            detail: { response },
            bubbles: true,
            composed: true,
          }),
        );
      }

      redirectAfterSubmit(response, isClosedCart = true) {
        const body = document.body;
        const isCartPage = body.classList.contains("template-cart");

        if (this.cartType === "drawer" && isCartPage) {
          const overlay = document.querySelector(
            ".wt__quick-buy--page-overlay",
          );
          overlay.classList.remove("wt__quick-buy--page-overlay--open");
          body.classList.remove("quick-buy-page-overlay");
        } else if (this.cartType === "drawer") {
          this.cart.renderContents(response, isClosedCart);
          return;
        }
        window.location = window.routes.cart_url;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message",
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
    },
  );
}
